import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ReportTestPage, ReportTestQuery } from './ci-mapping.model';

const DEFAULT_PAGE_SIZE = 100;

export type AssignmentAction = 'assign' | 'unassign';

export interface AssignmentFailure {
  messageKey: string;
  /** Die Zeile passt nicht mehr zum Stand des Servers; die Liste muss neu geladen werden. */
  stale: boolean;
}

const CONFLICTS: Record<string, AssignmentFailure> = {
  CASE_ALREADY_IN_EXECUTION: { messageKey: 'ciMapping.actionError.CASE_ALREADY_IN_EXECUTION', stale: false },
  ALREADY_ASSIGNED: { messageKey: 'ciMapping.actionError.ALREADY_ASSIGNED', stale: true },
  NOT_MANUALLY_ASSIGNED: { messageKey: 'ciMapping.actionError.NOT_MANUALLY_ASSIGNED', stale: true },
};

export function assignmentFailure(error: unknown, action: AssignmentAction): AssignmentFailure {
  if (error instanceof HttpErrorResponse) {
    const code: unknown = error.error?.code;
    if (error.status === 409 && typeof code === 'string' && CONFLICTS[code]) return CONFLICTS[code];
    if (error.status === 404) return { messageKey: 'ciMapping.actionError.notFound', stale: true };
    if (error.status === 403) return { messageKey: 'ciMapping.actionError.forbidden', stale: false };
  }
  return { messageKey: `ciMapping.actionError.${action}`, stale: false };
}

@Injectable({ providedIn: 'root' })
export class CiMappingService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}/api/v1/projects`;

  list(projectId: string, runId: string, query: ReportTestQuery): Observable<ReportTestPage> {
    let params = new HttpParams()
      .set('filter', query.filter)
      .set('page', query.page)
      .set('size', query.size ?? DEFAULT_PAGE_SIZE);
    if (query.executionId) {
      params = params.set('executionId', query.executionId);
    }
    if (query.status) {
      params = params.set('status', query.status);
    }
    if (query.q) {
      params = params.set('q', query.q);
    }
    return this.http.get<ReportTestPage>(this.reportTestsUrl(projectId, runId), { params });
  }

  assign(projectId: string, runId: string, reportTestId: string, testCaseId: string): Observable<void> {
    return this.http
      .post<unknown>(this.assignmentUrl(projectId, runId, reportTestId), { testCaseId })
      .pipe(map(() => undefined));
  }

  unassign(projectId: string, runId: string, reportTestId: string): Observable<void> {
    return this.http.delete<void>(this.assignmentUrl(projectId, runId, reportTestId));
  }

  private reportTestsUrl(projectId: string, runId: string): string {
    return `${this.baseUrl}/${encodeURIComponent(projectId)}/test-runs/${encodeURIComponent(runId)}/report-tests`;
  }

  private assignmentUrl(projectId: string, runId: string, reportTestId: string): string {
    return `${this.reportTestsUrl(projectId, runId)}/${encodeURIComponent(reportTestId)}/assignment`;
  }
}
